<template>
  <div id="phone-verification-container">
    <v-card
      id="phone-verification-card"
      ref="card"
    >
      <v-card-text class="phone-number">
        <p v-html="$t('components.phone_verification.cell_paragraph')" />
        <div id="phone-number-container">
          <div id="phone-number-input">
            <label>
              {{ $t('components.phone_verification.cell_label') }}
            </label>
            <v-text-field
              ref="phoneNumberInput"
              v-model="number"
              v-mask="phoneMask"
              :class="{valid: showPhoneNumberValid, invalid: showPhoneNumberInvalid}"
              class="phone-number-input"
              type="tel"
              outlined
              single-line
              dense
              disabled
              :rules="[phoneNumberRule]"
              :validate-on-blur="true"
              @input="changeNumber"
              @blur="blurNumber"
            />
          </div>
          <div
            ref="validationChips"
            class="phone-number-chip-container"
          >
            <v-chip
              v-if="validated"
              class="phone-number-chip"
              label
              color="#e8ffdb"
              text-color="#4ed002"
            >
              {{ $t('components.phone_verification.validated') }}
              <v-icon right>
                mdi-alert-circle
              </v-icon>
            </v-chip>
            <v-chip
              v-if="!validated"
              class="phone-number-chip"
              label
              color="#FFDADF"
              text-color="#D0021B"
            >
              {{ $t('components.phone_verification.not_validated') }}
              <v-icon right>
                mdi-alert-circle
              </v-icon>
            </v-chip>
          </div>
          <p
            v-if="requestCodeMessage"
            :class="`alert alert-${requestCodeMessage.type}`"
            v-html="requestCodeMessage.message"
          />
        </div>
        <div v-if="!validated">
          <div v-if="codeSent">
            <p v-html="$t('components.phone_verification.enter_sms_code')" />
            <v-form
              id="code-validation-form"
              ref="form"
              @submit.prevent="validateCode"
            >
              <div id="code-input-container">
                <label>
                  {{ $t('components.phone_verification.code_label') }}
                </label>
                <v-text-field
                  v-model="code"
                  v-mask="'####'"
                  placeholder="****"
                  type="tel"
                  outlined
                  single-line
                  dense
                  :class="{valid: showCodeValid, invalid: showCodeInvalid}"
                  :validate-on-blur="true"
                  :rules="[codeRule]"
                  @blur="blurCode"
                />
              </div>
              <v-card-actions class="validate-btn-container">
                <brazil-v-btn
                  class="validate-btn cta-btn"
                  type="action"
                  :depressed="true"
                  :submit="true"
                  :loading="codeSubmitting"
                >
                  {{ $t('components.phone_verification.validate') }}
                </brazil-v-btn>
              </v-card-actions>
              <p
                v-if="codeVerificationMessage"
                :class="`alert alert-${codeVerificationMessage.type}`"
                v-html="codeVerificationMessage.message"
              />
            </v-form>
            <v-divider />
            <div
              class="resend-link"
              @click="sendCode"
            >
              <p>
                {{ $t('components.phone_verification.never_received_code') }}
              </p>
              <span v-if="!requestingCode">
                <v-icon
                  color="#05a006"
                  left
                >
                  mdi-refresh
                </v-icon>
                <span class="resend-link-text">
                  {{ $t('components.phone_verification.resend_code') }}
                </span>
              </span>
              <span v-else>
                <v-progress-circular
                  indeterminate
                  color="#05a006"
                />
              </span>
            </div>
          </div>
          <div v-else>
            <brazil-v-btn
              class="cta-btn"
              type="action"
              :depressed="true"
              :loading="requestingCode"
              @click="sendCode"
            >
              {{ $t('components.phone_verification.request_code') }}
            </brazil-v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { VueMaskDirective } from 'v-mask';
import BrazilVBtn from './BrazilVBtn.vue';
import { phoneMask } from '../config/masks';
import captureSentryException from '../tracking/sentry/captureSentryException';
import railsAxios from '../lib/railsAxios';
import captureRenderBlockingException from '../tracking/helpers/captureRenderBlockingException';

export default {
  directives: {
    mask: VueMaskDirective,
  },
  components: {
    BrazilVBtn,
  },
  data() {
    return {
      phoneMask,
      number:                  '',
      validated:               false,
      codeSent:                false,
      codeSubmitted:           false,
      requestingCode:          false,
      code:                    '',
      codeSubmitting:          false,
      showPhoneNumberValid:    false,
      showPhoneNumberInvalid:  false,
      showCodeValid:           false,
      showCodeInvalid:         false,
      codeVerificationMessage: null,
      requestCodeMessage:      null,
    };
  },
  beforeMount() {
    try {
      this.number = this.$store.state.registration.reg1.main_phone;
    } catch (e) {
      captureSentryException(e);
    }
  },
  mounted() {
    try {
      this.$refs.phoneNumberInput.focus();
      if (this.validNumber(this.number)) {
        this.showPhoneNumberValid = true;
      }
    } catch (e) {
      // acceptable failure
      captureSentryException(e);
    }
  },
  methods: {
    sendCode() {
      this.requestCodeMessage = null;
      if (!this.validNumber(this.number)) {
        this.showPhoneNumberValid = false;
        this.showPhoneNumberInvalid = true;
        return;
      }

      this.requestingCode = true;
      railsAxios.post('/phone_verification/request_code', { which_phone: 'main_phone' })
        .then((response) => {
          if (!['success', 'active_code_request'].includes(response.data.status)) {
            this.showPhoneNumberValid = false;
            this.showPhoneNumberInvalid = true;
            this.$emit('validatePhoneNumber', false);
            this.requestCodeMessage = response.data.message;
            throw Error('invalid request code response during phone verification');
          }

          this.showPhoneNumberValid = true;
          this.showPhoneNumberInvalid = false;
          this.codeSent = true;
          this.codeSubmitted = false;
          this.$nextTick().then(() => {
            if (window.width < 768) {
              const scrollParams = { block: 'start', behavior: 'smooth' };
              this.$refs.validationChips.scrollIntoView(scrollParams);
            }
          });
        })
        .catch((e) => {
          captureRenderBlockingException(e);
          this.flash(this.$t('shared.generic_error'), 'error');
        })
        .finally(() => {
          this.requestingCode = false;
        });
    },
    validateCode() {
      this.codeSubmitted = true;
      if (!this.$refs.form.validate()) {
        this.showCodeInvalid = true;
        this.$emit('validatePhoneNumber', false);
        return;
      }

      this.codeSubmitting = true;
      const data = {
        which_phone: 'main_phone',
      };

      for (let i = 0; i < this.code.length; i += 1) {
        data[`main_phone_verification_${i + 1}`] = this.code.charAt(i);
      }

      this.codeVerificationMessage = null;
      railsAxios.post('/phone_verification/check_code', data)
        .then((response) => {
          if (response.data.status !== 'valid_code') {
            this.showCodeInvalid = true;
            this.validated = false;
            this.showCodeValid = false;
            this.$emit('validatePhoneNumber', false);
            this.codeVerificationMessage = response.data.message;
            return;
          }

          this.code = '';
          this.codeSent = false;
          this.validated = true;
          this.showCodeValid = true;
          this.showCodeInvalid = false;
          this.$emit('validatePhoneNumber', true);
        })
        .catch((e) => {
          captureRenderBlockingException(e);
          this.flash(this.$t('shared.generic_error'), 'error');
        })
        .finally(() => {
          this.codeSubmitting = false;
        });
    },
    changeNumber() {
      this.showCodeValid = false;
      this.validated = false;
      this.codeSent = false;
      this.code = '';
    },
    blurNumber() {
      if (this.validNumber(this.number)) {
        this.showPhoneNumberInvalid = false;
        this.showPhoneNumberValid = true;
      } else {
        this.showPhoneNumberInvalid = true;
        this.showPhoneNumberValid = false;
      }
    },
    blurCode() {
      if (this.code && this.code.length === 4) {
        this.showCodeInvalid = false;
        this.showCodeValid = true;
      } else {
        this.showCodeInvalid = true;
        this.showCodeValid = false;
      }
    },
    codeRule(code) {
      return code.length === 4 ? true : this.$t('components.phone_verification.invalid_code');
    },
    phoneNumberRule(numberString) {
      return this.validNumber(numberString) ? true : this.$t('components.phone_verification.invalid_number');
    },
    validNumber(numberString) {
      if (!numberString) {
        return false;
      }
      const number = numberString.replace(/\D/g, '');
      return number.length >= 10;
    },
  },
};
</script>
<style lang="scss">
#phone-verification-container {
  .phone-number-chip {
    width: 100%;
    justify-content: center;
    margin-bottom: 1em;
    font-weight: bold;
    min-height: 40px;
  }
  p {
    margin-bottom: 2em;
  }
  .resend-link {
    text-align: center;
    cursor: pointer;
    p {
      margin-bottom: 0.5em;
    }
    span {
      font-weight: bold;
      color: #05a006;
    }
    span.resend-link-text {
      text-decoration: underline;
    }
  }
  input {
    border: none;
    background-color: white;
    margin-bottom: 0;
    text-align: center;
  }
  .invalid, .alert-danger {
    color: rgb(208, 2, 27);
  }
  .invalid {
    .v-input__slot {
      border: 2px solid rgb(208, 2, 27);
    }
    fieldset {
      border: none;
    }
    .v-messages__wrapper {
      color: rgb(208, 2, 27);
    }
  }
  .alert-success, .valid {
    color: #05a006;
  }
  .valid {
    .v-input__slot {
      border: 2px solid #05a006;
    }
    fieldset {
      border: none;
    }
  }
  #phone-verification-card {
    margin: 2em auto;
    strong {
      font-weight: bold;
    }
    .cta-btn {
      text-transform: capitalize;
      color: rgb(84, 84, 84);
    }
    label {
      display: block;
      text-align: left;
    }
    hr {
      margin: 1.5em auto;
    }
    .validate-btn-container {
      padding: 0;
    }
  }
  #code-validation-form {
    display: block;
  }
  @media (min-width: 768px) {
    #phone-number-container {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;

      #phone-number-input, .phone-number-chip-container {
        width: 48%;
      }
      .phone-number-chip {
        margin-top: 22px;
      }
    }
    form#code-validation-form {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;

      .validate-btn-container {
        width: 48%;
        margin-top: -12px;
      }

      .alert {
        margin-top: 0;
      }
    }
    #code-input-container {
      width: 48%;
    }
  }
}
</style>
